/**
 * The open document.
 *
 * One map, open in one session, with everything that has to stay in step about it: the document,
 * the undo history, the journal, and who has asked to be told when any of it changes. Framework-
 * free, because the same engine has to serve a React canvas, a React Native screen and a test.
 *
 * The write path is the architecture's, in its order, and the order is the whole point:
 *
 *   1. validate and apply in memory   → the caller has an answer immediately
 *   2. notify subscribers             → the UI paints before anything touches a disk
 *   3. append to the journal          → durable from here on
 *   4. record in the undo history
 *   5. checkpoint if one is due
 *
 * **If step 3 fails, step 1 is undone.** That is the decision in this file most worth
 * understanding, because the comfortable alternative is wrong: leaving a visible edit that was
 * never journalled means the document and its journal disagree, the next checkpoint quietly
 * persists an edit no journal entry describes, and the user is shown work that a crash would
 * take. Reverting is jarring; losing an edit silently is the thing this whole product is built
 * not to do. The failure is returned, so the UI can say what happened.
 */
import { getNode, nodeCount as countNodes, } from '@orbit-mind/domain';
import { apply, applyAll, canRedo, canUndo, record, redo as redoStep, restamp, undo as undoStep, COMMAND_SCHEMA_VERSION, EMPTY_HISTORY, } from '@orbit-mind/editor-commands';
import { journalSaveState, openJournal, } from '@orbit-mind/offline-engine';
import { appError, err, ok, } from '@orbit-mind/shared-types';
/**
 * The sync half of the save state, before there is a sync layer.
 *
 * `online` is true because the network is not what is stopping anything in Phase 1. Everything
 * else is false, which derives `saved-locally` for a map with unsent edits — the honest answer
 * while no server exists. The one state this cannot honestly produce is `synced`, and
 * `hasLocalWork` on the journal status is what a UI checks before showing anything at all.
 */
export const NO_SYNC_LAYER = {
    online: true,
    syncInFlight: false,
    lastSyncFailed: false,
    hasConflict: false,
};
export async function openMap(options) {
    const opened = await openJournal({
        storage: options.storage,
        mapId: options.mapId,
        ...(options.base === undefined ? {} : { base: options.base }),
        ...(options.policy === undefined ? {} : { policy: options.policy }),
    });
    if (!opened.ok)
        return err(opened.error);
    const engine = createEngine({
        mapId: options.mapId,
        environment: options.environment,
        store: opened.value.store,
        document: opened.value.document,
        ...(options.onCheckpoint === undefined ? {} : { onCheckpoint: options.onCheckpoint }),
    });
    return ok({
        engine,
        journal: opened.value.store,
        outcome: opened.value.outcome,
        fallback: opened.value.fallback,
        unreadable: opened.value.unreadable,
        skipped: opened.value.replayed.skipped,
    });
}
function createEngine(input) {
    const { mapId, environment, store } = input;
    /** Runs the checkpoint reaction, awaiting it when there is something to await. */
    async function announceCheckpoint(written) {
        const reaction = input.onCheckpoint?.(written);
        if (reaction !== undefined)
            await reaction;
    }
    let document = input.document;
    let history = EMPTY_HISTORY;
    let sync = NO_SYNC_LAYER;
    let lastError = null;
    let closed = false;
    const documentListeners = new Set();
    const nodeListeners = new Map();
    const statusListeners = new Set();
    // The store's own status changes without an edit — a write landing, an acknowledgement
    // arriving — and the save state has to follow those too, not only dispatches.
    const unsubscribeFromStore = store.subscribe(() => {
        notifyStatus();
    });
    /**
     * Bumped by anything that could change the status, so `status()` can hand back the same object
     * until something actually did.
     *
     * Not an optimisation. A subscriber that compares snapshots by identity — which is what
     * `useSyncExternalStore` does, and what any sane external store contract does — would see a
     * fresh object on every read and re-render for ever.
     */
    let statusVersion = 0;
    let statusSnapshot = null;
    let snapshotVersion = -1;
    function status() {
        if (statusSnapshot !== null && snapshotVersion === statusVersion)
            return statusSnapshot;
        const journal = store.status();
        statusSnapshot = {
            mapId,
            saveState: journalSaveState(journal, sync),
            journal,
            canUndo: canUndo(history),
            canRedo: canRedo(history),
            undoing: history.past[history.past.length - 1]?.command.kind ?? null,
            redoing: history.future[0]?.command.kind ?? null,
            nodeCount: countNodes(document),
            lastError,
        };
        snapshotVersion = statusVersion;
        return statusSnapshot;
    }
    function notifyStatus() {
        statusVersion += 1;
        if (statusListeners.size === 0)
            return;
        const snapshot = status();
        for (const listener of statusListeners)
            listener(snapshot);
    }
    function notifyDocument(change) {
        for (const listener of documentListeners)
            listener(change);
    }
    /** Only the nodes someone is watching, and only the ones whose object actually changed. */
    function notifyNodes(before, after) {
        for (const [nodeId, listeners] of nodeListeners) {
            const previous = getNode(before, nodeId);
            const current = getNode(after, nodeId);
            if (previous === current)
                continue;
            for (const listener of listeners)
                listener(current);
        }
    }
    function nextMeta() {
        return {
            operationId: environment.ids.operationId(),
            timestamp: environment.clock.now(),
            authorId: environment.authorId,
            schemaVersion: COMMAND_SCHEMA_VERSION,
        };
    }
    function refuseIfClosed() {
        if (!closed)
            return null;
        return appError('INTERNAL', 'the map is closed', { details: { mapId }, retryable: false });
    }
    /**
     * Journals an already-applied change, and puts the document back if it cannot.
     *
     * Shared by every path that changes the document — dispatch, batch, undo, redo — so there is
     * one place where "durable" is decided and one rollback to get right.
     */
    async function commit(change) {
        document = change.after;
        notifyDocument({ document, command: change.reportAs, reverted: false });
        notifyNodes(change.before, document);
        notifyStatus();
        const appended = await store.appendAll(change.commands);
        if (!appended.ok) {
            const failed = document;
            document = change.before;
            lastError = appended.error;
            notifyDocument({ document, command: change.reportAs, reverted: true });
            notifyNodes(failed, document);
            notifyStatus();
            return err(appended.error);
        }
        history = change.nextHistory(history);
        lastError = null;
        // Awaited rather than left running: the edit is already durable, so this only delays the
        // *answer*, never the safety of the work. A checkpoint that failed while nobody was looking
        // would leave recovery slower and no one any the wiser.
        const checkpointed = await store.maybeCheckpoint(document, environment.clock.now());
        if (!checkpointed.ok)
            lastError = checkpointed.error;
        else if (checkpointed.value !== null)
            await announceCheckpoint(document);
        notifyStatus();
        return ok({
            document,
            command: change.reportAs,
            checkpointed: checkpointed.ok && checkpointed.value !== null,
        });
    }
    async function dispatchAll(commands) {
        const refusal = refuseIfClosed();
        if (refusal !== null)
            return err(refusal);
        const last = commands[commands.length - 1];
        if (last === undefined) {
            return err(appError('VALIDATION_FAILED', 'dispatch was given no commands', {
                details: { mapId },
                retryable: false,
            }));
        }
        const before = document;
        const applied = applyAll(before, commands);
        if (!applied.ok)
            return err(applied.error);
        // `applyAll` returns the inverses reversed, because undoing a batch means undoing its steps
        // in the opposite order. History is recorded forwards, so they are paired back up here.
        const forwardInverses = [...applied.value.inverses].reverse();
        return commit({
            before,
            after: applied.value.document,
            commands,
            nextHistory: (current) => commands.reduce((stack, command, index) => {
                const inverse = forwardInverses[index];
                if (inverse === undefined)
                    return stack;
                // Never labelled, so coalescing cannot merge two different commands of a batch into one
                // entry — that merge keeps the first inverse and the last command, which would undo the
                // whole batch and redo only its tail.
                return record(stack, { command, inverse, label: null });
            }, current),
            reportAs: last,
        });
    }
    async function dispatch(command, options) {
        const refusal = refuseIfClosed();
        if (refusal !== null)
            return err(refusal);
        const before = document;
        const applied = apply(before, command);
        if (!applied.ok)
            return err(applied.error);
        return commit({
            before,
            after: applied.value.document,
            commands: [command],
            nextHistory: (current) => options?.recordHistory === false
                ? current
                : record(current, {
                    command,
                    inverse: applied.value.inverse,
                    label: options?.label ?? null,
                }),
            reportAs: command,
        });
    }
    return {
        mapId,
        document: () => document,
        node: (nodeId) => getNode(document, nodeId),
        status,
        nextMeta,
        context: () => ({ document, environment, meta: nextMeta() }),
        dispatch,
        dispatchAll,
        async undo() {
            const refusal = refuseIfClosed();
            if (refusal !== null)
                return err(refusal);
            const entry = history.past[history.past.length - 1];
            if (entry === undefined)
                return ok(null);
            // Re-stamped before it is applied, so the document in memory and the command in the journal
            // agree — and so the journal does not mistake the undo for the edit it undoes. See
            // `restamp` in `editor-commands` for why that is not optional.
            const inverse = restamp(entry.inverse, nextMeta());
            const adjusted = {
                past: [...history.past.slice(0, -1), { ...entry, inverse }],
                future: history.future,
            };
            const step = undoStep(document, adjusted);
            if (step === null)
                return ok(null);
            return commit({
                before: document,
                after: step.document,
                commands: [step.applied],
                nextHistory: () => step.history,
                reportAs: step.applied,
            });
        },
        async redo() {
            const refusal = refuseIfClosed();
            if (refusal !== null)
                return err(refusal);
            const entry = history.future[0];
            if (entry === undefined)
                return ok(null);
            // A redo re-applies the original command, so it needs its own identity for the same reason.
            const command = restamp(entry.command, nextMeta());
            const adjusted = {
                past: history.past,
                future: [{ ...entry, command }, ...history.future.slice(1)],
            };
            const step = redoStep(document, adjusted);
            if (step === null)
                return ok(null);
            return commit({
                before: document,
                after: step.document,
                commands: [step.applied],
                nextHistory: () => step.history,
                reportAs: step.applied,
            });
        },
        subscribe(listener) {
            documentListeners.add(listener);
            return () => {
                documentListeners.delete(listener);
            };
        },
        subscribeToNode(nodeId, listener) {
            const listeners = nodeListeners.get(nodeId) ?? new Set();
            listeners.add(listener);
            nodeListeners.set(nodeId, listeners);
            return () => {
                listeners.delete(listener);
                // Dropped once empty, so a long session does not accumulate an entry per node ever
                // rendered — which on a 10,000-node map is the difference between watching 200 and 10,000.
                if (listeners.size === 0)
                    nodeListeners.delete(nodeId);
            };
        },
        subscribeToStatus(listener) {
            statusListeners.add(listener);
            return () => {
                statusListeners.delete(listener);
            };
        },
        setSyncStatus(next) {
            sync = next;
            notifyStatus();
        },
        async acknowledgeRevision(revision) {
            if (closed)
                return ok(undefined);
            document = { ...document, map: { ...document.map, serverRevision: revision } };
            const saved = await store.checkpoint(document, environment.clock.now());
            if (!saved.ok)
                return err(saved.error);
            await announceCheckpoint(document);
            notifyStatus();
            return ok(undefined);
        },
        async checkpointNow() {
            const result = await store.checkpoint(document, environment.clock.now());
            if (!result.ok) {
                lastError = result.error;
                notifyStatus();
                return err(result.error);
            }
            await announceCheckpoint(document);
            notifyStatus();
            return ok(undefined);
        },
        async close() {
            if (closed)
                return ok(undefined);
            closed = true;
            // Everything handed over lands, and then a checkpoint, so the next open replays nothing it
            // does not have to. Closing is the one moment we know there is time for it.
            await store.flush();
            const result = await store.checkpoint(document, environment.clock.now());
            if (result.ok)
                await announceCheckpoint(document);
            unsubscribeFromStore();
            documentListeners.clear();
            nodeListeners.clear();
            statusListeners.clear();
            return result.ok ? ok(undefined) : err(result.error);
        },
    };
}
//# sourceMappingURL=engine.js.map